import { useEffect, useState } from 'react';
import Lottie from 'lottie-react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { Loader2 } from 'lucide-react';
import {
  STICKER_MIME_TYPE,
  STICKER_PREVIEW_IMAGE_CLASS,
  convertWhatsappWasStickerPayload,
  extractLottieAnimationData,
  fetchAuthorizedMediaBlob,
  normalizeStickerBlobForDisplay,
} from '@/lib/realtime/message-attachments';

/** Lottie preview box — explicit px so lottie-web gets a stable viewport. */
const STICKER_LOTTIE_PX = 144;

function cloneAnimationData(data) {
  return JSON.parse(JSON.stringify(data));
}

/**
 * Resolve presentation mode from blob response (authoritative) with fallback to hint from metadata.
 * @param {Blob} blob
 * @param {string} hint
 */
function effectiveStickerMode(blob, hint) {
  const mt = String(blob.type || '').toLowerCase();
  if (mt.startsWith('video/')) {
    return 'video';
  }
  if (mt.startsWith('image/')) {
    return 'webp';
  }
  return hint;
}

const stickerFrameClass =
  'inline-flex rounded-2xl bg-linear-to-b from-primary/6 to-transparent p-1 ring-1 ring-border/30 dark:from-primary/12 dark:ring-border/40';

/**
 * Renders a sticker: animated WebP, Lottie JSON, dotLottie (.lottie), short video, or raw .was bytes.
 *
 * @param {{ path: string; label: string; rowKey: string; presentationMode: 'lottie-json' | 'dotlottie' | 'webp' | 'video' | 'was' }} props
 */
export function StickerSlot({ path, label, rowKey, presentationMode }) {
  const [state, setState] = useState({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    const objectUrls = [];

    async function run() {
      try {
        const blob = await fetchAuthorizedMediaBlob(path);
        if (cancelled) {
          return;
        }

        const ab = await blob.arrayBuffer();
        const blobType = String(blob.type || '').toLowerCase();
        const hint = effectiveStickerMode(blob, presentationMode);

        const tryWasDecode =
          presentationMode === 'was' ||
          blobType === STICKER_MIME_TYPE ||
          blobType.endsWith('/was');

        if (tryWasDecode) {
          const conv = await convertWhatsappWasStickerPayload(ab);
          if (conv) {
            if (conv.mimeType === 'application/json') {
              try {
                const text = new TextDecoder('utf-8', { fatal: false }).decode(conv.buffer);
                const data = extractLottieAnimationData(JSON.parse(text));
                if (data && !cancelled) {
                  setState({ status: 'ready', render: 'lottie', animationData: cloneAnimationData(data) });
                  return;
                }
              } catch {
                /* fall through */
              }
            }
            if (conv.mimeType === 'application/zip') {
              const url = URL.createObjectURL(new Blob([conv.buffer], { type: 'application/zip' }));
              objectUrls.push(url);
              if (!cancelled) {
                setState({ status: 'ready', render: 'dotlottie', url });
              }
              return;
            }
            if (conv.mimeType.startsWith('video/')) {
              const url = URL.createObjectURL(new Blob([conv.buffer], { type: conv.mimeType }));
              objectUrls.push(url);
              if (!cancelled) {
                setState({ status: 'ready', render: 'video', url });
              }
              return;
            }
            if (conv.mimeType.startsWith('image/')) {
              const url = URL.createObjectURL(new Blob([conv.buffer], { type: conv.mimeType }));
              objectUrls.push(url);
              if (!cancelled) {
                setState({ status: 'ready', render: 'image', url });
              }
              return;
            }
          }
        }

        if (hint === 'video') {
          const url = URL.createObjectURL(new Blob([ab], { type: blob.type || 'video/mp4' }));
          objectUrls.push(url);
          if (!cancelled) {
            setState({ status: 'ready', render: 'video', url });
          }
          return;
        }

        if (hint === 'webp') {
          const url = URL.createObjectURL(new Blob([ab], { type: blob.type || 'image/webp' }));
          objectUrls.push(url);
          if (!cancelled) {
            setState({ status: 'ready', render: 'image', url });
          }
          return;
        }

        if (hint === 'was') {
          const u8 = new Uint8Array(ab);
          if (u8[0] === 0x7b) {
            try {
              const data = extractLottieAnimationData(
                JSON.parse(new TextDecoder('utf-8', { fatal: false }).decode(ab)),
              );
              if (data && !cancelled) {
                setState({ status: 'ready', render: 'lottie', animationData: cloneAnimationData(data) });
                return;
              }
            } catch {
              /* fall through */
            }
          }
          const normalized = await normalizeStickerBlobForDisplay(
            new Blob([ab], { type: blob.type || 'application/octet-stream' }),
          );
          const url = URL.createObjectURL(normalized.blob);
          objectUrls.push(url);
          if (!cancelled) {
            setState({
              status: 'ready',
              render: normalized.mode === 'video' ? 'video' : 'image',
              url,
            });
          }
          return;
        }

        const normalized = await normalizeStickerBlobForDisplay(
          new Blob([ab], { type: blob.type || 'application/octet-stream' }),
        );
        const url = URL.createObjectURL(normalized.blob);
        objectUrls.push(url);
        if (!cancelled) {
          setState({
            status: 'ready',
            render: normalized.mode === 'video' ? 'video' : 'image',
            url,
          });
        }
      } catch (_e) {
        if (!cancelled) {
          setState({ status: 'error' });
        }
      }
    }

    void run();
    return () => {
      cancelled = true;
      objectUrls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [path, rowKey, presentationMode]);

  if (state.status === 'error') {
    return (
      <span className="inline-flex items-center rounded-full border border-dashed border-border/60 px-2 py-1 text-[0.65rem] text-muted-foreground">
        Sticker unavailable
      </span>
    );
  }

  if (state.status === 'loading') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-muted/50 px-2.5 py-1 text-[0.65rem] text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
        Sticker…
      </span>
    );
  }

  const inner =
    state.render === 'lottie' ? (
      <div
        className="flex max-h-[min(9rem,42vw)] max-w-[min(9rem,42vw)] shrink-0 items-center justify-center overflow-hidden rounded-lg"
        style={{ width: STICKER_LOTTIE_PX, height: STICKER_LOTTIE_PX }}
      >
        <Lottie
          animationData={state.animationData}
          loop
          autoplay
          style={{ width: STICKER_LOTTIE_PX, height: STICKER_LOTTIE_PX, display: 'block' }}
        />
      </div>
    ) : state.render === 'dotlottie' ? (
      <div
        className="flex max-h-[min(9rem,42vw)] max-w-[min(9rem,42vw)] shrink-0 items-center justify-center overflow-hidden rounded-lg"
        style={{ width: STICKER_LOTTIE_PX, height: STICKER_LOTTIE_PX }}
      >
        <DotLottieReact
          src={state.url}
          loop
          autoplay
          style={{ width: STICKER_LOTTIE_PX, height: STICKER_LOTTIE_PX, display: 'block' }}
        />
      </div>
    ) : state.render === 'video' ? (
      <video
        src={state.url}
        className={STICKER_PREVIEW_IMAGE_CLASS}
        autoPlay
        loop
        muted
        playsInline
      />
    ) : (
      <img src={state.url} alt="" className={STICKER_PREVIEW_IMAGE_CLASS} draggable={false} />
    );

  return (
    <span className={stickerFrameClass} title={label} aria-label={`Sticker: ${label}`}>
      {inner}
    </span>
  );
}

/**
 * Local file sticker preview after URLs / JSON are resolved (composer).
 *
 * @param {{
 *   presentationMode: 'lottie-json' | 'dotlottie' | 'webp' | 'video' | 'was';
 *   url?: string;
 *   animationData?: unknown;
 *   fileName: string;
 * }} props
 */
export function ComposerStickerPreview({ presentationMode, url, animationData, fileName }) {
  const lottiePayload =
    presentationMode === 'lottie-json' && animationData ? extractLottieAnimationData(animationData) : null;

  const inner =
    presentationMode === 'lottie-json' && lottiePayload ? (
      <div
        className="flex max-h-[min(9rem,42vw)] max-w-[min(9rem,42vw)] shrink-0 items-center justify-center overflow-hidden rounded-lg"
        style={{ width: STICKER_LOTTIE_PX, height: STICKER_LOTTIE_PX }}
      >
        <Lottie
          animationData={cloneAnimationData(lottiePayload)}
          loop
          autoplay
          style={{ width: STICKER_LOTTIE_PX, height: STICKER_LOTTIE_PX, display: 'block' }}
        />
      </div>
    ) : presentationMode === 'dotlottie' && url ? (
      <div
        className="flex max-h-[min(9rem,42vw)] max-w-[min(9rem,42vw)] shrink-0 items-center justify-center overflow-hidden rounded-lg"
        style={{ width: STICKER_LOTTIE_PX, height: STICKER_LOTTIE_PX }}
      >
        <DotLottieReact
          src={url}
          loop
          autoplay
          style={{ width: STICKER_LOTTIE_PX, height: STICKER_LOTTIE_PX, display: 'block' }}
        />
      </div>
    ) : presentationMode === 'video' && url ? (
      <video src={url} className={STICKER_PREVIEW_IMAGE_CLASS} autoPlay loop muted playsInline />
    ) : url ? (
      <img src={url} alt="" className={STICKER_PREVIEW_IMAGE_CLASS} draggable={false} />
    ) : (
      <span className="inline-flex items-center gap-1.5 text-[0.65rem] text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
        Sticker…
      </span>
    );

  return (
    <span className={stickerFrameClass} title={fileName} aria-label={`Sticker: ${fileName}`}>
      {inner}
    </span>
  );
}
